import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { IftaLabelModule } from 'primeng/iftalabel';
import { forkJoin } from 'rxjs';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, isThirdPartyVirtualWarehouse, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingDispatch, WarehousingDispatchReservation, WarehousingItem, WarehousingMasterData } from '../warehousing.model';
import { WarehousingLabelPrintService } from '../warehousing-label-print.service';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';

type ScanMode = 'pick' | 'stage' | 'load';

type ReservationRow = {
  itemId: number;
  warehouseId?: number;
  zoneId?: number;
  conditionId?: number;
  reservation: WarehousingDispatchReservation;
};

@Component({
  selector: 'app-warehousing-dispatch-scan',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, BreadcrumbModule, ButtonModule, InputGroupModule, InputGroupAddonModule,
    InputTextModule, ProgressBarModule, TableModule, TagModule, ToastModule, IftaLabelModule,
    PageFormSectionComponent, PageHeroComponent, PageMetricStripComponent, PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-dispatch-scan.component.html'
})
export class WarehousingDispatchScanComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private labelPrintService = inject(WarehousingLabelPrintService);
  private messageService = inject(MessageService);

  isLoading = false;
  isScanning = false;
  isPrinting = false;
  dispatch?: WarehousingDispatch;
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  scanMode: ScanMode = 'pick';
  scanValue = '';
  selectedUnitIds = new Set<number>();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Dispatch Scan', 'Dispatches', '/warehousing/dispatch');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.loadDispatch();
  }

  loadDispatch(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      dispatches: this.warehousingService.getDispatches(),
      items: this.warehousingService.getItems(),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.dispatch = (response.dispatches.data || []).find(row => row.id === id);
        this.items = response.items.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.syncScanMode();
        this.breadcrumbItems = warehousingBreadcrumb(
          this.dispatch?.dispatchNumber || 'Dispatch Scan',
          'Dispatches',
          '/warehousing/dispatch'
        );
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  setScanMode(mode: ScanMode): void {
    this.scanMode = mode;
  }

  submitScan(): void {
    if (this.isScanning) return;
    const stockUnitCode = (this.scanValue || '').trim();
    if (!this.dispatch?.id || !stockUnitCode || !this.supportsStockLedgerWorkflow() || !this.isGateOpen()) {
      return;
    }

    const request = { stockUnitCode };
    this.isScanning = true;
    const operation = this.scanMode === 'pick'
      ? this.warehousingService.scanDispatchPick(this.dispatch.id, request)
      : this.scanMode === 'stage'
        ? this.warehousingService.scanDispatchStage(this.dispatch.id, request)
        : this.warehousingService.scanDispatchLoad(this.dispatch.id, request);

    operation.subscribe({
      next: response => {
        this.dispatch = response.data;
        this.scanValue = '';
        this.syncScanMode();
        this.messageService.add({
          severity: 'success',
          summary: 'Scan Recorded',
          detail: `${stockUnitCode} marked as ${this.scanMode}.`
        });
        this.isScanning = false;
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Scan Failed',
          detail: error?.error?.message || 'Unable to record the scan.'
        });
        this.isScanning = false;
      }
    });
  }

  async printSelectedLabels(): Promise<void> {
    const stockUnitIds = Array.from(this.selectedUnitIds);
    if (!stockUnitIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Select Units', detail: 'Select at least one reserved unit.' });
      return;
    }
    this.printLabels(stockUnitIds, `${this.dispatch?.dispatchNumber || 'dispatch'}-selected-labels.zpl`);
  }

  async printAllLabels(): Promise<void> {
    const stockUnitIds = this.reservationRows()
      .map(row => row.reservation.stockUnitId)
      .filter((value, index, values) => values.indexOf(value) === index);
    if (!stockUnitIds.length) {
      return;
    }
    this.printLabels(stockUnitIds, `${this.dispatch?.dispatchNumber || 'dispatch'}-all-labels.zpl`);
  }

  toggleUnitSelection(stockUnitId: number, checked: boolean): void {
    if (checked) {
      this.selectedUnitIds.add(stockUnitId);
    } else {
      this.selectedUnitIds.delete(stockUnitId);
    }
  }

  reservationRows(): ReservationRow[] {
    return (this.dispatch?.lines || []).flatMap(line => (line.reservations || []).map(reservation => ({
      itemId: line.itemId,
      warehouseId: reservation.stockUnitWarehouseId || line.warehouseId,
      zoneId: reservation.stockUnitZoneId || line.zoneId,
      conditionId: reservation.stockUnitConditionId || line.conditionId,
      reservation
    })));
  }

  metrics(): PageMetric[] {
    const reservations = this.reservationRows().map(row => row.reservation);
    return [
      { label: 'Reserved Units', value: reservations.length },
      { label: 'Picked', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 1).length, tone: 'info' },
      { label: 'Staged', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 2).length, tone: 'warn' },
      { label: 'Loaded', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 3).length, tone: 'success' }
    ];
  }

  itemName(itemId: number): string {
    const item = this.items.find(row => row.id === itemId);
    return itemIdentityLabel(item);
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return (this.masterData.WAREHOUSE || []).find(item => item.id === id);
  }

  supportsStockLedgerWorkflow(): boolean {
    return !(this.dispatch?.lines || []).some(line => isThirdPartyVirtualWarehouse(this.warehouseById(line.warehouseId)));
  }

  isGateOpen(): boolean {
    return !this.dispatch?.gatedByTransferId || this.dispatch.crossDockGateOpen === true;
  }

  displayDateTime(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  isSelected(stockUnitId: number): boolean {
    return this.selectedUnitIds.has(stockUnitId);
  }

  reservationBindingLabel(reservation: WarehousingDispatchReservation): string {
    return (reservation.reservationBindingMode
      || (this.isInheritedReservation(reservation) ? 'UPSTREAM_LOCKED' : 'DISPATCH_LOCKED'))
      .replace(/_/g, ' ');
  }

  reservationSourceLabel(reservation: WarehousingDispatchReservation): string {
    if (!this.isInheritedReservation(reservation)) {
      return 'Dispatch reserve';
    }
    const sourceType = reservation.sourceReservationType || 'Inherited reservation';
    const sourceId = reservation.sourceReservationId ? ` #${reservation.sourceReservationId}` : '';
    return `${sourceType}${sourceId}`;
  }

  statusSeverity = statusSeverity;

  private async printLabels(stockUnitIds: number[], filename: string): Promise<void> {
    this.isPrinting = true;
    this.warehousingService.printStockUnitLabels({ stockUnitIds, template: 'WAREHOUSE_STOCK_4X6' }).subscribe({
      next: async blob => {
        const mode = await this.labelPrintService.printOrDownload(blob, filename);
        this.messageService.add({
          severity: 'success',
          summary: mode === 'printed' ? 'Labels Printed' : 'Labels Downloaded',
          detail: mode === 'printed' ? 'Labels were sent to the default Zebra printer.' : 'ZPL labels were downloaded.'
        });
        this.isPrinting = false;
      },
      error: () => this.isPrinting = false
    });
  }

  private syncScanMode(): void {
    const lifecycle = (this.dispatch?.lifecycleStatus || this.dispatch?.status || '').toUpperCase();
    if (lifecycle === 'STAGED') {
      this.scanMode = 'load';
      return;
    }
    if (lifecycle === 'PICKED') {
      this.scanMode = 'stage';
      return;
    }
    if (lifecycle === 'LOADED') {
      this.scanMode = 'load';
      return;
    }
    this.scanMode = 'pick';
  }

  private scanRank(scanStatus?: string): number {
    switch ((scanStatus || 'PENDING').toUpperCase()) {
      case 'PICKED': return 1;
      case 'STAGED': return 2;
      case 'LOADED': return 3;
      default: return 0;
    }
  }

  private isInheritedReservation(reservation: WarehousingDispatchReservation): boolean {
    return !!(reservation.sourceReservationType || reservation.sourceReservationId);
  }
}
